import { useEffect, useState } from "react";
import {
    FaPlus,
    FaEdit,
    FaTrash,
    FaSearch,
    FaSpinner,
    FaArrowLeft,
    FaMicrophone,
    FaDownload,
    FaEyeSlash,
    FaEye
} from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { getMenuPermissions } from "../utils/auth";

import "./POILocations.css";

const API_URL = process.env.REACT_APP_API_URL;


function POILocations() {

    // Mirrors require_menu_permission("poi_locations") on the backend.
    // Upload and Generate-MP3 are both POST calls, so they're gated
    // on `add` the same way the primary Add button is.
    const perms = getMenuPermissions("poi_locations");

    const emptyForm = {
        id: "",
        waypoint_id: "",
        name: "",
        category: "general",
        display_order: 0,
        short_desc: "",
        full_desc: "",
        image_url: "",
        video_url: "",
        audio_url: "",
        is_active: true
    };

    const [poiList, setPoiList] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [search, setSearch] = useState("");

    const [showForm, setShowForm] = useState(false);
    const [isEdit, setIsEdit] = useState(false);

    const [form, setForm] = useState(emptyForm);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    // Populated from Category Master (POI Location and Category
    // Master share the same permission gate, so this is always
    // reachable wherever the POI form itself is).
    const [categories, setCategories] = useState([]);

    // Tracks which of image/video/audio is currently mid-upload, so
    // each file input can show its own spinner independently.
    const [uploading, setUploading] = useState({ image: false, video: false, audio: false });

    // "Generate MP3" section - deployment-level visibility comes from
    // the server (ttsEnabled, see loadFeatureFlags), independent of
    // showSection, which is just this admin's own personal
    // collapse/expand preference for the current session.
    const [ttsEnabled, setTtsEnabled] = useState(false);
    const [ttsConfigured, setTtsConfigured] = useState(false);
    const [showTtsSection, setShowTtsSection] = useState(true);
    const [isGeneratingAudio, setIsGeneratingAudio] = useState(false);

    useEffect(() => {
        loadPoi();
        loadCategories();
        loadFeatureFlags();
    }, []);

    useEffect(() => {

        const value = search.toLowerCase();

        setFiltered(

            poiList.filter(item =>
                item.name.toLowerCase().includes(value) ||
                item.waypoint_id.toLowerCase().includes(value) ||
                (item.category || "").toLowerCase().includes(value)
            )

        );

    }, [search, poiList]);

    const loadPoi = async () => {

        setIsLoading(true);

        try {

            const res = await fetch(`${API_URL}/portal/poi-locations`);

            if (!res.ok) {
                throw new Error(`Request failed with status ${res.status}`);
            }

            const data = await res.json();

            if (Array.isArray(data)) {

                setPoiList(data);

            } else if (data.status) {

                setPoiList(data.data || []);

            } else {

                setPoiList([]);

                toast.error(data.message || "Failed to load POI locations");

            }

        } catch (err) {

            console.error(err);

            setPoiList([]);

            toast.error("Unable to load POI locations. Please try again.");

        } finally {

            setIsLoading(false);

        }

    };

    const loadCategories = async () => {

        try {

            const res = await fetch(`${API_URL}/portal/poi-categories/active`);

            const data = await res.json();

            if (data.status) {

                setCategories(data.data || []);

            }

        } catch (err) {

            console.error("Failed to load categories:", err);

        }

    };

    const loadFeatureFlags = async () => {

        try {

            const res = await fetch(`${API_URL}/portal/poi-locations/feature-flags`);

            const data = await res.json();

            if (data.status) {

                setTtsEnabled(!!data.tts_enabled);

                setTtsConfigured(!!data.tts_configured);

            }

        } catch (err) {

            console.error("Failed to load feature flags:", err);

        }

    };

    const handleGenerateAudio = async () => {

        if (!form.full_desc || !form.full_desc.trim()) {

            toast.warn("Write a Full Description first - that's what gets converted to audio.");
            return;

        }

        setIsGeneratingAudio(true);

        try {

            const res = await fetch(`${API_URL}/portal/poi-locations/generate-audio`, {

                method: "POST",

                headers: { "Content-Type": "application/json" },

                body: JSON.stringify({ text: form.full_desc })

            });

            const data = await res.json();

            if (res.ok && data.status) {

                setForm(prev => ({ ...prev, audio_url: data.url }));

                toast.success("Audio generated successfully");

            } else {

                toast.error(data.detail || data.message || "Failed to generate audio");

            }

        } catch (err) {

            console.error("Failed to generate audio:", err);

            toast.error("Something went wrong generating the audio");

        } finally {

            setIsGeneratingAudio(false);

        }

    };

    const handleChange = (e) => {

        const { name, value, type, checked } = e.target;

        setForm({
            ...form,
            [name]: type === "checkbox" ? checked : value
        });

    };

    const handleFileUpload = async (mediaType, file) => {

        if (!file) return;

        setUploading(prev => ({ ...prev, [mediaType]: true }));

        try {

            const body = new FormData();

            body.append("file", file);

            const res = await fetch(`${API_URL}/portal/poi-locations/upload/${mediaType}`, {

                method: "POST",

                body

            });

            const data = await res.json();

            if (res.ok && data.status) {

                setForm(prev => ({ ...prev, [`${mediaType}_url`]: data.url }));

                toast.success(`${mediaType[0].toUpperCase()}${mediaType.slice(1)} uploaded successfully`);

            } else {

                toast.error(data.detail || data.message || `Failed to upload ${mediaType}`);

            }

        } catch (err) {

            console.error(`Failed to upload ${mediaType}:`, err);

            toast.error(`Something went wrong uploading the ${mediaType}`);

        } finally {

            setUploading(prev => ({ ...prev, [mediaType]: false }));

        }

    };

    const openAdd = () => {

        setIsEdit(false);

        setForm(emptyForm);

        setShowForm(true);

    };

    const openEdit = (item) => {

        setIsEdit(true);

        setForm({
            ...emptyForm,
            ...item
        });

        setShowForm(true);

    };

    const closeForm = () => {

        if (isSaving) return;

        setShowForm(false);

        setForm(emptyForm);

    };

    const savePoi = async () => {

        if (!form.waypoint_id || !form.name) {

            toast.warn("Waypoint ID and Name are required");
            return;

        }

        setIsSaving(true);

        const payload = {
            ...form,
            display_order: Number(form.display_order) || 0
        };

        try {

            const res = await fetch(`${API_URL}/portal/poi-locations`, {

                method: isEdit ? "PUT" : "POST",

                headers: {
                    "Content-Type": "application/json"
                },

                body: JSON.stringify(payload)

            });

            const data = await res.json();

            if (res.ok && data.status) {

                setShowForm(false);

                setForm(emptyForm);

                toast.success(
                    isEdit
                        ? "POI location updated successfully"
                        : "POI location added successfully"
                );

                loadPoi();

            } else {

                toast.error(data.message || "Failed to save POI location");

            }

        } catch (err) {

            console.error("Failed to save POI location:", err);

            toast.error("Something went wrong while saving the POI location");

        } finally {

            setIsSaving(false);

        }

    };

    const deletePoi = async (id) => {

        const ok = window.confirm(
            "Are you sure you want to delete this POI location?"
        );

        if (!ok) return;

        setDeletingId(id);

        try {

            const res = await fetch(`${API_URL}/portal/poi-locations/${id}`, {

                method: "DELETE"

            });

            const data = await res.json();

            if (res.ok && data.status) {

                toast.success("POI location deleted successfully");

                loadPoi();

            } else {

                toast.error(data.message || "Failed to delete POI location");

            }

        } catch (err) {

            console.error("Failed to delete POI location:", err);

            toast.error("Something went wrong while deleting the POI location");

        } finally {

            setDeletingId(null);

        }

    };

    return (

        <div className="page">

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
            />

            <div className="page-header">

                <h2>POI Locations</h2>

                {

                    !showForm && perms.add &&

                    <button
                        type="button"
                        className="add-btn"
                        onClick={openAdd}
                    >

                        <FaPlus />

                        Add POI Location

                    </button>

                }

            </div>

            {

                showForm ? (

                    <div className="inline-form-container">

                        <div className="inline-form-header">

                            <button
                                type="button"
                                className="back-btn"
                                onClick={closeForm}
                                disabled={isSaving}
                            >

                                <FaArrowLeft />
                                Back to list

                            </button>

                            <h3>
                                {isEdit ? "Update POI Location" : "Add POI Location"}
                            </h3>

                        </div>

                        <div className="inline-form-grid">

                            <input
                                name="waypoint_id"
                                placeholder="Waypoint ID (must match the robot's map point)"
                                value={form.waypoint_id}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <input
                                name="name"
                                placeholder="Display Name"
                                value={form.name}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <select
                                name="category"
                                value={form.category}
                                onChange={handleChange}
                                disabled={isSaving}
                            >

                                <option value="">Select a category...</option>

                                {categories.map(cat => (

                                    <option key={cat.id} value={cat.name}>{cat.name}</option>

                                ))}

                                {/* Keeps a previously-saved category selectable even if it was
                                    since deactivated/deleted from Category Master, so editing an
                                    existing POI doesn't silently blank out its category. */}
                                {form.category && !categories.some(c => c.name === form.category) && (

                                    <option value={form.category}>{form.category} (no longer in Category Master)</option>

                                )}

                            </select>

                            <input
                                name="display_order"
                                type="number"
                                placeholder="Display Order"
                                value={form.display_order}
                                onChange={handleChange}
                                disabled={isSaving}
                            />

                            <div className="full-width media-upload-row">

                                <label className="media-upload-field">

                                    <span className="media-upload-label">Image</span>

                                    <div className="media-upload-controls">

                                        {perms.add &&
                                        <input
                                            type="file"
                                            accept="image/*"
                                            disabled={isSaving || uploading.image}
                                            onChange={(e) => handleFileUpload("image", e.target.files[0])}
                                        />
                                        }

                                        {uploading.image && <FaSpinner className="spin" />}

                                        {form.image_url && (
                                            <a href={form.image_url} target="_blank" rel="noreferrer" className="media-preview-link">
                                                View current
                                            </a>
                                        )}

                                    </div>

                                </label>

                                <label className="media-upload-field">

                                    <span className="media-upload-label">Video</span>

                                    <div className="media-upload-controls">

                                        {perms.add &&
                                        <input
                                            type="file"
                                            accept="video/*"
                                            disabled={isSaving || uploading.video}
                                            onChange={(e) => handleFileUpload("video", e.target.files[0])}
                                        />
                                        }

                                        {uploading.video && <FaSpinner className="spin" />}

                                        {form.video_url && (
                                            <a href={form.video_url} target="_blank" rel="noreferrer" className="media-preview-link">
                                                View current
                                            </a>
                                        )}

                                    </div>

                                </label>

                                <label className="media-upload-field">

                                    <span className="media-upload-label">Audio</span>

                                    <div className="media-upload-controls">

                                        {perms.add &&
                                        <input
                                            type="file"
                                            accept="audio/*"
                                            disabled={isSaving || uploading.audio}
                                            onChange={(e) => handleFileUpload("audio", e.target.files[0])}
                                        />
                                        }

                                        {uploading.audio && <FaSpinner className="spin" />}

                                        {form.audio_url && (
                                            <a href={form.audio_url} target="_blank" rel="noreferrer" className="media-preview-link">
                                                View current
                                            </a>
                                        )}

                                    </div>

                                </label>

                            </div>

                            <textarea
                                name="short_desc"
                                placeholder="Short description (shown on kiosk cards)"
                                value={form.short_desc || ""}
                                onChange={handleChange}
                                disabled={isSaving}
                                className="full-width"
                                rows={2}
                            />

                            <textarea
                                name="full_desc"
                                placeholder="Full description (shown on detail view)"
                                value={form.full_desc || ""}
                                onChange={handleChange}
                                disabled={isSaving}
                                className="full-width"
                                rows={3}
                            />

                            {

                                ttsEnabled &&

                                <div className="full-width tts-section">

                                    <button
                                        type="button"
                                        className="tts-section-toggle"
                                        onClick={() => setShowTtsSection(!showTtsSection)}
                                    >
                                        {showTtsSection ? <FaEyeSlash /> : <FaEye />}
                                        {showTtsSection ? "Hide" : "Show"} audio narration (private)
                                    </button>

                                    {

                                        showTtsSection &&

                                        <div className="tts-section-body">

                                            <p className="tts-section-hint">
                                                Generate an MP3 narration from the Full Description above,
                                                using a standard Indian-accent voice. This section is only
                                                visible to admins here - it's not shown to passengers.
                                            </p>

                                            {

                                                !ttsConfigured &&

                                                <p className="tts-section-warning">
                                                    Text-to-speech isn't available on this server right
                                                    now - it may be unable to reach the TTS service
                                                    (network/firewall), or was disabled by a SuperAdmin.
                                                </p>

                                            }

                                            <div className="tts-section-actions">

                                                {perms.add &&
                                                <button
                                                    type="button"
                                                    className="add-btn"
                                                    onClick={handleGenerateAudio}
                                                    disabled={isSaving || isGeneratingAudio}
                                                >
                                                    {
                                                        isGeneratingAudio
                                                            ? <><FaSpinner className="spin" /> Generating...</>
                                                            : <><FaMicrophone /> Generate MP3</>
                                                    }
                                                </button>
                                                }

                                                {

                                                    form.audio_url &&

                                                    <a
                                                        href={form.audio_url}
                                                        download
                                                        className="add-btn tts-download-btn"
                                                    >
                                                        <FaDownload /> Download MP3
                                                    </a>

                                                }

                                            </div>

                                            {

                                                form.audio_url &&

                                                <audio controls src={form.audio_url} className="tts-audio-preview">
                                                    Your browser doesn't support inline audio playback.
                                                </audio>

                                            }

                                        </div>

                                    }

                                </div>

                            }

                            <label className="checkbox-field full-width">
                                <input
                                    type="checkbox"
                                    name="is_active"
                                    checked={!!form.is_active}
                                    onChange={handleChange}
                                    disabled={isSaving}
                                />
                                Active (visible to passengers)
                            </label>

                        </div>

                        <div className="inline-form-actions">

                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={closeForm}
                                disabled={isSaving}
                            >
                                Cancel
                            </button>

                            <button
                                type="button"
                                className="save-btn"
                                onClick={savePoi}
                                disabled={isSaving}
                            >

                                {
                                    isSaving
                                        ? <><FaSpinner className="spin" /> Saving...</>
                                        : isEdit
                                            ? "Update POI Location"
                                            : "Save POI Location"
                                }

                            </button>

                        </div>

                    </div>

                ) : (

                    <>

                        <div className="search-box">

                            <FaSearch />

                            <input
                                placeholder="Search by name, waypoint ID, category..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />

                        </div>

                        <div className="table-container">

                            <table>

                                <thead>

                                    <tr>
                                        <th>Waypoint ID</th>
                                        <th>Name</th>
                                        <th>Category</th>
                                        <th>Used By</th>
                                        <th>Order</th>
                                        <th>Status</th>
                                        <th style={{ width: "120px" }}>Action</th>
                                    </tr>

                                </thead>

                                <tbody>

                                    {

                                        isLoading ?

                                            <tr>
                                                <td colSpan="7" style={{ textAlign: "center", padding: "40px" }}>
                                                    <FaSpinner className="spin" /> Loading POI locations...
                                                </td>
                                            </tr>

                                            :

                                            filtered.length === 0 ?

                                                <tr>
                                                    <td colSpan="7" style={{ textAlign: "center", padding: "40px" }}>
                                                        No Records Found
                                                    </td>
                                                </tr>

                                                :

                                                filtered.map(item => (

                                                    <tr key={item.id}>

                                                        <td>{item.waypoint_id}</td>

                                                        <td>{item.name}</td>

                                                        <td>{item.category}</td>

                                                        <td>

                                                            {

                                                                item.used_by_robots && item.used_by_robots.length > 0 ? (

                                                                    <span
                                                                        className="poi-used-by-badge"
                                                                        title={`Used as the reception point for: ${item.used_by_robots.map(r => r.robot_name || r.robot_sn).join(", ")}`}
                                                                    >
                                                                        {item.used_by_robots.map(r => r.robot_name || r.robot_sn).join(", ")}
                                                                    </span>

                                                                ) : (

                                                                    <span className="poi-not-used">Not used</span>

                                                                )

                                                            }

                                                        </td>

                                                        <td>{item.display_order}</td>

                                                        <td>
                                                            <span className={`status-pill ${item.is_active ? "active" : "inactive"}`}>
                                                                {item.is_active ? "Active" : "Inactive"}
                                                            </span>
                                                        </td>

                                                        <td>

                                                            <div className="action-buttons">

                                                                {perms.edit &&
                                                                <button
                                                                    type="button"
                                                                    className="icon-btn edit-btn"
                                                                    title="Edit POI location"
                                                                    aria-label="Edit POI location"
                                                                    onClick={() => openEdit(item)}
                                                                    disabled={deletingId === item.id}
                                                                >
                                                                    <FaEdit />
                                                                </button>
                                                                }

                                                                {perms.delete &&
                                                                <button
                                                                    type="button"
                                                                    className="icon-btn delete-btn"
                                                                    title="Delete POI location"
                                                                    aria-label="Delete POI location"
                                                                    onClick={() => deletePoi(item.id)}
                                                                    disabled={deletingId === item.id}
                                                                >
                                                                    {
                                                                        deletingId === item.id
                                                                            ? <FaSpinner className="spin" />
                                                                            : <FaTrash />
                                                                    }
                                                                </button>
                                                                }

                                                            </div>

                                                        </td>

                                                    </tr>

                                                ))

                                    }

                                </tbody>

                            </table>

                        </div>

                    </>

                )

            }

        </div>

    );

}

export default POILocations;
